/**
 * Persistent chrome: brand, store switcher, per-store nav, signed-in identity.
 *
 * WHY the switcher lives in the header: an agency managing several shops changes store far more
 * often than it changes screen, so the store is the outer axis of navigation and the section
 * tabs sit under it.
 */
import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useConnections } from "../stores";
import { useSession } from "../session";

const STORE_TABS: readonly { to: string; label: string; end?: boolean }[] = [
  { to: "", label: "Overview", end: true },
  { to: "payouts", label: "Payouts" },
  { to: "discrepancies", label: "Discrepancies" },
  { to: "disputes", label: "Disputes" },
  { to: "held-funds", label: "Held funds" },
  { to: "unsettled", label: "Unsettled" },
  { to: "fee-schedule", label: "Fees" },
  { to: "billing", label: "Billing" },
];

export function Shell({ connectionId, children }: { connectionId: string | null; children: ReactNode }): ReactNode {
  const { connections } = useConnections();
  const { session, signOut } = useSession();
  const navigate = useNavigate();

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-left">
          <NavLink to="/" className="brand">PayoutAudit</NavLink>
          {connections.length > 0 ? (
            <label className="switcher">
              <span className="sr-only">Store</span>
              <select
                className="select"
                value={connectionId ?? ""}
                onChange={(e) => navigate(e.target.value ? `/stores/${e.target.value}` : "/")}
              >
                {connectionId === null ? <option value="">All stores</option> : null}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <div className="topbar-right">
          {session.user ? <span className="who" title={`Tenant ${session.user.tenant_id}`}>{session.user.email}</span> : null}
          <button type="button" className="btn btn-quiet" onClick={() => { void signOut(); }}>Sign out</button>
        </div>
      </header>

      {connectionId !== null ? (
        <nav className="tabs" aria-label="Store sections">
          {STORE_TABS.map((tab) => (
            <NavLink
              key={tab.label}
              to={`/stores/${connectionId}/${tab.to}`.replace(/\/$/, "")}
              end={tab.end ?? false}
              className={({ isActive }) => `tab${isActive ? " tab-active" : ""}`}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      ) : null}

      <main className="page">{children}</main>
    </div>
  );
}
